import { useCallback, useEffect, useRef, useState } from "react";

import { kaiApi } from "@/services/kaiApi";

/** Human-readable status shown while a given MCP prefetch tool is running. */
const TOOL_STATUS: Record<string, string> = {
  api_daily_ops_brief: "Aggregating today's activity…",
  get_booking: "Loading booking…",
  get_booking_timeline: "Loading timeline…",
  get_booking_graph_mail_thread: "Loading email thread…",
  get_entity_embedding: "Loading context…",
};

export interface KaiEntitySummaryState {
  text: string;
  cachedAt: string | null;
  loading: boolean;
  streaming: boolean;
  error: string | null;
  status: string | null;
  regenerate: () => void;
}

interface SsePayload {
  content?: string;
  delta?: string;
  name?: string;
  message?: string;
}

/**
 * Load or generate a Kai entity summary for `(entityType, entityId)`. Hits the
 * cache first; streams a fresh summary on a miss or explicit regenerate.
 *
 * Passing an empty `entityId` or `enabled = false` keeps the hook idle.
 */
export function useKaiEntitySummary(
  entityType: string,
  entityId: string,
  enabled: boolean,
): KaiEntitySummaryState {
  const active = enabled && entityId.length > 0;
  const [text, setText] = useState("");
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(active);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const generate = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!active) return;
      const force = opts?.force === true;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      setStatus("Gathering data…");
      if (force) {
        setText("");
        setCachedAt(null);
      }

      try {
        if (!force) {
          const cached = await kaiApi.getCachedEntitySummary(
            entityType,
            entityId,
          );
          if (controller.signal.aborted) return;
          if (cached) {
            setText(cached.summary);
            setCachedAt(cached.cached_at);
            setLoading(false);
            setStreaming(false);
            setStatus(null);
            return;
          }
        }

        setStreaming(true);
        let accumulated = "";

        await kaiApi.streamEntitySummary(
          entityType,
          entityId,
          (event) => {
            let data: SsePayload = {};
            try {
              data = JSON.parse(event.data) as SsePayload;
            } catch {
              return;
            }

            switch (event.type) {
              case "tool_start":
                if (data.name) {
                  setStatus(TOOL_STATUS[data.name] ?? "Gathering data…");
                }
                break;
              case "text_delta":
                accumulated += data.delta ?? "";
                setText(accumulated);
                setStatus(null);
                break;
              case "text":
                accumulated = data.content ?? accumulated;
                setText(accumulated);
                setStatus(null);
                break;
              case "done": {
                const finalText = data.content ?? accumulated;
                setText(finalText);
                setCachedAt(new Date().toISOString());
                setStreaming(false);
                setLoading(false);
                setStatus(null);
                break;
              }
              case "error":
                setError(data.message || "Could not generate the insight.");
                setStreaming(false);
                setLoading(false);
                setStatus(null);
                break;
            }
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;
        setStreaming(false);
        setLoading(false);
        setStatus(null);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        const statusCode = (err as Error & { status?: number }).status;
        setError(
          statusCode === 403
            ? "Kai insights are not enabled for this organization."
            : err instanceof Error
              ? err.message
              : "Could not generate the insight.",
        );
        setStreaming(false);
        setLoading(false);
        setStatus(null);
      }
    },
    [active, entityType, entityId],
  );

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return;
    }
    void generate();
    return () => {
      abortRef.current?.abort();
    };
  }, [active, generate]);

  const regenerate = useCallback(() => {
    void generate({ force: true });
  }, [generate]);

  return { text, cachedAt, loading, streaming, error, status, regenerate };
}
